import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { OutlookRecipient } from "../types/resource";

interface AttendeesState {
  requiredAttendees: OutlookRecipient[];
  optionalAttendees: OutlookRecipient[];
  allAttendees: OutlookRecipient[];
  isLoading: boolean;
  error: string | null;

  setRequiredAttendees: (attendees: OutlookRecipient[]) => void;
  setOptionalAttendees: (attendees: OutlookRecipient[]) => void;
  addRequiredAttendee: (attendee: OutlookRecipient) => void;
  addOptionalAttendee: (attendee: OutlookRecipient) => void;
  removeRequiredAttendee: (email: string) => void;
  removeOptionalAttendee: (email: string) => void;
  moveToRequired: (email: string) => void;
  moveToOptional: (email: string) => void;
  clearAllAttendees: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateAllAttendees: () => void;
}

const initialState = {
  requiredAttendees: [],
  optionalAttendees: [],
  allAttendees: [],
  isLoading: false,
  error: null,
};

export const useAttendeesStore = create<AttendeesState>()(
  devtools((set, get) => ({
    ...initialState,

    setRequiredAttendees: (requiredAttendees) => {
      set({ requiredAttendees }, false, "setRequiredAttendees");
      get().updateAllAttendees();
    },

    setOptionalAttendees: (optionalAttendees) => {
      set({ optionalAttendees }, false, "setOptionalAttendees");
      get().updateAllAttendees();
    },

    addRequiredAttendee: (attendee) => {
      set((state) => {
        const exists = state.requiredAttendees.some(
          (a) => a.Email.toLowerCase() === attendee.Email.toLowerCase()
        );
        if (exists) return state;

        // Remove from optional if exists
        const filteredOptional = state.optionalAttendees.filter(
          (a) => a.Email.toLowerCase() !== attendee.Email.toLowerCase()
        );

        return {
          requiredAttendees: [...state.requiredAttendees, attendee],
          optionalAttendees: filteredOptional,
        };
      }, false, "addRequiredAttendee");
      get().updateAllAttendees();
    },

    addOptionalAttendee: (attendee) => {
      set((state) => {
        const exists = state.optionalAttendees.some(
          (a) => a.Email.toLowerCase() === attendee.Email.toLowerCase()
        );
        if (exists) return state;

        // Remove from required if exists
        const filteredRequired = state.requiredAttendees.filter(
          (a) => a.Email.toLowerCase() !== attendee.Email.toLowerCase()
        );

        return {
          optionalAttendees: [...state.optionalAttendees, attendee],
          requiredAttendees: filteredRequired,
        };
      }, false, "addOptionalAttendee");
      get().updateAllAttendees();
    },

    removeRequiredAttendee: (email) => {
      set((state) => ({
        requiredAttendees: state.requiredAttendees.filter(
          (a) => a.Email.toLowerCase() !== email.toLowerCase()
        ),
      }), false, "removeRequiredAttendee");
      get().updateAllAttendees();
    },

    removeOptionalAttendee: (email) => {
      set((state) => ({
        optionalAttendees: state.optionalAttendees.filter(
          (a) => a.Email.toLowerCase() !== email.toLowerCase()
        ),
      }), false, "removeOptionalAttendee");
      get().updateAllAttendees();
    },

    moveToRequired: (email) => {
      set((state) => {
        const attendee = state.optionalAttendees.find(
          (a) => a.Email.toLowerCase() === email.toLowerCase()
        );
        if (!attendee) return state;

        return {
          optionalAttendees: state.optionalAttendees.filter(
            (a) => a.Email.toLowerCase() !== email.toLowerCase()
          ),
          requiredAttendees: [...state.requiredAttendees, attendee],
        };
      }, false, "moveToRequired");
      get().updateAllAttendees();
    },

    moveToOptional: (email) => {
      set((state) => {
        const attendee = state.requiredAttendees.find(
          (a) => a.Email.toLowerCase() === email.toLowerCase()
        );
        if (!attendee) return state;

        return {
          requiredAttendees: state.requiredAttendees.filter(
            (a) => a.Email.toLowerCase() !== email.toLowerCase()
          ),
          optionalAttendees: [...state.optionalAttendees, attendee],
        };
      }, false, "moveToOptional");
      get().updateAllAttendees();
    },

    clearAllAttendees: () => {
      set({
        requiredAttendees: [],
        optionalAttendees: [],
        allAttendees: [],
      }, false, "clearAllAttendees");
    },

    setLoading: (isLoading) => set({ isLoading }, false, "setLoading"),

    setError: (error) => set({ error }, false, "setError"),

    updateAllAttendees: () => {
      const { requiredAttendees, optionalAttendees } = get();
      const allAttendees = [...requiredAttendees, ...optionalAttendees];
      
      // Remove duplicates based on email
      const uniqueAttendees = Array.from(
        new Map(
          allAttendees.map(attendee => [
            attendee.Email.toLowerCase(),
            attendee
          ])
        ).values()
      );

      set({ allAttendees: uniqueAttendees }, false, "updateAllAttendees");
    },
  }), {
    name: 'attendees-store',
  })
);
